import type React from "react";

export const GREEN = "#10b981"; export const GREEN_DIM = "#10b98112"; export const GREEN_BORDER = "#10b98130";
export const BLUE = "#3b82f6"; export const BLUE_DIM = "#3b82f612"; export const BLUE_BORDER = "#3b82f630";
export const PURPLE = "#a855f7"; export const PURPLE_DIM = "#a855f712"; export const PURPLE_BORDER = "#a855f730";
export const AMBER = "#f59e0b"; export const AMBER_DIM = "#f59e0b12"; export const AMBER_BORDER = "#f59e0b30";
export const CYAN = "#06b6d4"; export const CYAN_DIM = "#06b6d412"; export const CYAN_BORDER = "#06b6d430";
export const ROSE = "#f43f5e"; export const ROSE_DIM = "#f43f5e12"; export const ROSE_BORDER = "#f43f5e30";
export const TEAL = "#14b8a6"; export const TEAL_DIM = "#14b8a612"; export const TEAL_BORDER = "#14b8a630";
export const INDIGO = "#6366f1"; export const INDIGO_DIM = "#6366f112"; export const INDIGO_BORDER = "#6366f130";

export const inter: React.CSSProperties = { fontFamily: "Inter, sans-serif" };
export const mono: React.CSSProperties = { fontFamily: "'Courier New', monospace" };
export const poppins: React.CSSProperties = { fontFamily: "Poppins, sans-serif" };

export interface TrainingModule {
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  color: string;
  colorDim: string;
  colorBorder: string;
  lessons: string[];
  actions: Array<{ label: string; href: (token: string) => string }>;
}

export const TRAINING_MODULES: TrainingModule[] = [
  {
    id: "welcome-setup",
    icon: "🚀",
    title: "Welcome & Setup",
    subtitle: "Get your studio profile ready for your first clients.",
    color: GREEN, colorDim: GREEN_DIM, colorBorder: GREEN_BORDER,
    lessons: [
      "Update your studio name, address, and contact information",
      "Upload your logo and set your hero/team photos",
      "Set your business hours and hours note",
      "Review your studio description, slogan, and homepage tagline",
      "Connect your Google Review link to encourage client reviews",
      "Add your Instagram and Facebook profile URLs",
    ],
    actions: [
      { label: "Studio Settings", href: t => `/admin/studio/${t}` },
      { label: "Account Hub", href: t => `/admin/account/${t}` },
    ],
  },
  {
    id: "managing-bookings",
    icon: "📋",
    title: "Managing Bookings",
    subtitle: "Learn the full booking lifecycle from request to close-out.",
    color: BLUE, colorDim: BLUE_DIM, colorBorder: BLUE_BORDER,
    lessons: [
      "How to confirm or decline a new booking request",
      "Sending deposit requests and tracking payment status",
      "How to reschedule a client appointment",
      "Closing out a completed session (add actual cost and duration)",
      "Understanding booking reference codes and status labels",
      "How to handle no-shows and reactivate cancelled bookings",
    ],
    actions: [
      { label: "Today's Schedule", href: t => `/admin/today/${t}` },
      { label: "Upcoming Bookings", href: t => `/admin/upcoming-bookings/${t}` },
      { label: "Deposits", href: t => `/admin/deposits/${t}` },
    ],
  },
  {
    id: "daily-digest",
    icon: "📧",
    title: "Your Daily Digest",
    subtitle: "Understand your morning email and business reporting.",
    color: PURPLE, colorDim: PURPLE_DIM, colorBorder: PURPLE_BORDER,
    lessons: [
      "What sections appear in your daily digest email",
      "How to add or remove digest email recipients",
      "Using one-click action buttons in the digest email",
      "Understanding the business performance metrics section",
      "How to request a fresh digest link if yours expires",
      "Customizing which sections each recipient sees",
    ],
    actions: [
      { label: "Email Settings", href: t => `/admin/email-settings/${t}` },
      { label: "Booking Analytics", href: t => `/admin/booking-analytics/${t}` },
    ],
  },
  {
    id: "artists-availability",
    icon: "👥",
    title: "Artists & Availability",
    subtitle: "Manage who takes bookings, when, and at what rates.",
    color: AMBER, colorDim: AMBER_DIM, colorBorder: AMBER_BORDER,
    lessons: [
      "How to add a new artist profile with bio, photo, and rates",
      "Setting per-artist hourly rate ranges for the cost estimator",
      "Toggling artist availability: Open, Closed, or By Request Only",
      "Blocking specific dates for vacations or studio closures",
      "Reordering artists on the public booking page",
      "Setting up artist-specific Teamup calendar sync",
    ],
    actions: [
      { label: "Manage Artists", href: t => `/admin/artists/${t}` },
      { label: "Availability Settings", href: t => `/admin/availability/${t}` },
    ],
  },
  {
    id: "flash-gallery",
    icon: "⚡",
    title: "Flash Gallery",
    subtitle: "Create urgency and showcase ready-to-book designs.",
    color: ROSE, colorDim: ROSE_DIM, colorBorder: ROSE_BORDER,
    lessons: [
      "What flash designs are and how they're shown to clients",
      "Adding a flash design with image, price, size, and description",
      "Setting expiry dates to create scarcity and urgency",
      "Marking a design as sold or temporarily unavailable",
      "Reordering flash designs on the gallery page",
      "Best practices: using flash to fill slow booking periods",
    ],
    actions: [
      { label: "Flash Designs", href: t => `/admin/flash/${t}` },
    ],
  },
  {
    id: "client-management",
    icon: "👤",
    title: "Client Management",
    subtitle: "Build your client list and keep relationships active.",
    color: CYAN, colorDim: CYAN_DIM, colorBorder: CYAN_BORDER,
    lessons: [
      "Browsing, searching, and filtering your contact list",
      "Importing existing client data via CSV upload",
      "Sending a broadcast email campaign to subscribers",
      "Identifying lapsed clients who are overdue for re-engagement",
      "Managing the booking waitlist and notifying clients of openings",
      "Reviewing incoming contact form messages and contact inbox",
    ],
    actions: [
      { label: "Contacts", href: t => `/admin/contacts/${t}` },
      { label: "Lapsed Clients", href: t => `/admin/lapsed-clients/${t}` },
      { label: "Waitlist", href: t => `/admin/waitlist/${t}` },
    ],
  },
  {
    id: "payments-deposits",
    icon: "💰",
    title: "Payments & Deposits",
    subtitle: "Collect deposits, track payments, and set your pricing.",
    color: TEAL, colorDim: TEAL_DIM, colorBorder: TEAL_BORDER,
    lessons: [
      "How the deposit system works and when to request one",
      "Setting your default deposit amount in studio settings",
      "Tracking deposit paid, pending, and waived statuses",
      "Using the cost estimator to give clients accurate price ranges",
      "Configuring your estimator pricing tiers and complexity levels",
      "Handling deposit disputes, credits, or waivers",
    ],
    actions: [
      { label: "Deposits", href: t => `/admin/deposits/${t}` },
      { label: "Pricing Estimate Config", href: t => `/admin/estimate-model/${t}` },
    ],
  },
  {
    id: "admin-security",
    icon: "🔒",
    title: "Admin Security",
    subtitle: "Keep your account safe and understand access controls.",
    color: INDIGO, colorDim: INDIGO_DIM, colorBorder: INDIGO_BORDER,
    lessons: [
      "Understanding how token-based access links work",
      "Viewing the audit log to see all admin actions",
      "Revoking and rotating access tokens when a link is compromised",
      "Setting up IP whitelisting for extra security",
      "Reviewing the token sharing alert system",
      "Emergency controls: what to do if you lose access",
    ],
    actions: [
      { label: "Audit Log", href: t => `/admin/audit-log/${t}` },
      { label: "Token Security", href: t => `/admin/security/${t}` },
    ],
  },
];
